import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { v2 } from "oc-threejs/short3";
import {
  assertSegmentIstLückenlosGefüllt,
  assertWarnungen,
  createStreckeWithMauerUndBreite,
  getInspiredArrangement,
  logParts,
  resetArrangement,
  setSternFormMitWinkelnLeerraumRechts,
} from "./utils";
import { Segment } from "../arrangement/Segment";
import { ZElement } from "../arrangement/ZElement";
import { löscheNachbarElementeUmMindestplatzZuErreichen } from "../arrangement/arrangementFunctions";
import { streckenZuGraph } from "../arrangement/grundrissFunctions";
import { insertTor } from "../components/grundriss/TorDragging";

export default {
  name: "TestDoppeltor",
};

const isVoreinstellungMitEAntrieb =
  APP.richMetadata.findAuswahlwert("E_Antrieb_mit")?.Voreinstellung;

const mindestbreiteDoppeltor = (eantrieb) =>
  (eantrieb ? 1 : 0.6) * 2 + (eantrieb ? 0.06 : 0.04) + (0.0125 + 0.08) * 2 + 0.01;

export const testEinElementInEinerGefülltenStreckeWirdAufDoppeltorGeändertRechts: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [5]);
    agt.fillIndividuellSegmentsWithElements();
    const s = agt.segmente[0];
    const element = _.last(s.getElemente()) as any;
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
      schaffePlatzFallsDoppeltor: false,
    });
    agt.update3d();
    agt.update3d();
    assertDoppeltorInSzene(element, s);
    assert.equals(
      s.getElemente().length - 1,
      s.getElemente().indexOf(element),
      "Das Doppeltor soll ganz rechts stehen"
    );
    assertSegmentIstLückenlosGefüllt(s);
  };

export const testLöscheAutomatisch1: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [1]);
  const s = agt.segmente[0];
  agt.update3d();
  löscheNachbarElementeUmMindestplatzZuErreichen(s.empties[0], 3.5, true);
  agt.update3d();

  const kante = agt.grundriss.kanten[0];
  kante.ecken[1].punkt.x = kante.ecken[0].punkt.x + 10 - 0.08;
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  löscheNachbarElementeUmMindestplatzZuErreichen(s.parts[3], 3.65, true);
  assert.deepEquals(
    [
      {
        x0: 0.02,
        x1: 0.8,
        gapLinks: -0.02,
        gapRechts: -0.02,
        type: "Element",
        snap: 1,
        breite: 0.78,
        lichteWeite: 0.74,
        ecke: false,
      },
      {
        x0: 0.84,
        x1: 2.62,
        gapLinks: -0.02,
        gapRechts: -0.02,
        type: "Element",
        snap: 0,
        breite: 1.78,
        lichteWeite: 1.74,
        ecke: false,
      },
      {
        x0: 2.68,
        x1: 8.06,
        gapLinks: 0,
        gapRechts: 0,
        type: "Empty",
        snap: 1,
        breite: 5.38,
        lichteWeite: 0,
        ecke: false,
      },
      {
        x0: 8.12,
        x1: 9.9,
        gapLinks: -0.02,
        gapRechts: -0.02,
        type: "Element",
        snap: 1,
        breite: 1.78,
        lichteWeite: 1.74,
        ecke: false,
      },
    ],
    logParts(s, s.getParts())
  );
};

export const testInAusreichendGroßemSegmentWirdLeerraumGefüllt: Test = async () => {
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [4]);
  const s = agt.segmente[0];
  agt.update3d();
  APP.arrangement.setSelectedObjects(s.empties);
  agt.update3d();
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const element = s.getElemente()[0];
  agt.update3d();
  assertDoppeltorInSzene(element, s, isVoreinstellungMitEAntrieb);
  assert.equals(1, s.getElemente().length);
  assert.equals(3.61, element.breite);
  assert.equals(3.6 + (isVoreinstellungMitEAntrieb ? 0.06 : 0.04), element.lichteWeite);
  assert.equals(0, s.getElemente().indexOf(element), "Das Doppeltor soll das erste Element sein");
};

export const testInMittelgroßemSegmentWirdLeerraumGefüllt: Test = async () => {
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [3]);
  const s = agt.segmente[0];
  agt.update3d();
  APP.arrangement.setSelectedObjects(s.empties);
  agt.update3d();
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const element = s.getElemente()[0];
  assertDoppeltorInSzene(element, s, isVoreinstellungMitEAntrieb);
  assert.equals(1, s.getElemente().length);
  assertSegmentIstLückenlosGefüllt(s);
  assert.equals(0, s.getElemente().indexOf(element), "Das Doppeltor soll das erste Element sein");
};

export const testInSchmaleremSegmentWirdDerLeerraumGefüllt: Test = async () => {
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const agt = APP.arrangement;
  resetArrangement();
  const breite = mindestbreiteDoppeltor(isVoreinstellungMitEAntrieb);
  agt.grundriss.setStandardForm("Strecke", [breite]);
  const s = agt.segmente[0];
  agt.update3d();
  APP.arrangement.setSelectedObjects(s.empties);
  agt.update3d();
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const element = s.getElemente()[0];
  assertDoppeltorInSzene(element, s, isVoreinstellungMitEAntrieb);
  assert.equals(1, s.getElemente().length);
  assertSegmentIstLückenlosGefüllt(s);
  assert.equals(0, s.getElemente().indexOf(element), "Das Doppeltor soll das erste Element sein");
};

export const testDerSchmaleLeerraumNebenElementWirdGefüllt: Test = async () => {
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const agt = APP.arrangement;
  resetArrangement();
  const breite =
    (isVoreinstellungMitEAntrieb ? 1.55 : 1.78) -
    0.08 +
    mindestbreiteDoppeltor(isVoreinstellungMitEAntrieb);
  agt.grundriss.setStandardForm("Strecke", [breite]);
  const s = agt.segmente[0];
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente()[0].uiRemove();
  agt.update3d();
  APP.arrangement.setSelectedObjects(s.empties);
  agt.update3d();
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const element = s.getElemente()[0];
  assertDoppeltorInSzene(element, s, isVoreinstellungMitEAntrieb);
  assert.equals(
    1,
    s.getElemente().length,
    "Das Doppeltor soll das danebenstehende Element löschen"
  );
  assertSegmentIstLückenlosGefüllt(s);
};

export const testEinElementInEinerGefülltenStrecke5mWirdAufDoppeltorGeändertLinks: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [5]);
    agt.fillIndividuellSegmentsWithElements();
    const s = agt.segmente[0];
    const element = s.getElemente()[0];
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
    });
    const doppeltor = agt.selectedObjects[0];
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    assertDoppeltorInSzene(doppeltor, s);
    assert.equals(
      0,
      s.getElemente().indexOf(doppeltor),
      "Das Doppeltor soll das erste Element sein"
    );
    assert.deepEquals([], s.getÜberstände(), "Es soll keine Überstände geben");
  };

export const testEinElementInEinerGefülltenStrecke10mWirdAufDoppeltorGeändertLinks: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [10]);
    agt.fillIndividuellSegmentsWithElements();
    const s = agt.segmente[0];
    const element = s.getElemente()[0];
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
    });
    const doppeltor = agt.selectedObjects[0];
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    assertDoppeltorInSzene(doppeltor, s);
    assert.equals(
      0,
      s.getElemente().indexOf(doppeltor),
      "Das Doppeltor soll das erste Element sein"
    );
    assert.deepEquals([], s.getÜberstände(), "Es soll keine Überstände geben");
  };

export const testEinElementInEinerGefülltenStrecke10mMitAufschraubPfostenWirdAufDoppeltorGeändert: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [10]);
    agt.fillIndividuellSegmentsWithElements();
    const s = agt.segmente[0];
    const bodenbefestigungAufgeschraubt = APP.richMetadata.findAuswahlwert(
      "Aufgeschraubt auf festem Boden"
    );
    s.getPfosten().forEach((p) => (p.bodenbefestigungAuswahlwert = bodenbefestigungAufgeschraubt));
    agt.update3d();
    const element = s.getElemente()[2];
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
    });
    const doppeltor = agt.selectedObjects[0];
    agt.update3d();
    assertDoppeltorInSzene(doppeltor, s, true);
    assert.equals(3.45, doppeltor.breite);
    const bodenbefestigungEinbetoniert = APP.richMetadata.findAuswahlwert("Einbetoniert im Boden");
    assert.isTrue(
      doppeltor
        .getPfosten()
        .every((p) => p.bodenbefestigungAuswahlwert === bodenbefestigungEinbetoniert)
    );
    assertWarnungen(agt, []);
  };

export const testEinElementLinksInEinerAnsonstenLeerenStreckeWirdAufDoppeltorGeändert: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [5]);
    const s = agt.segmente[0];
    const element = agt.createDefaultElement();
    s.addPart(element);
    element.snap = 0;
    agt.update3d();
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
    });
    element.behalteLichteweite(
      () => (element.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    assertDoppeltorInSzene(element, s);
    assert.equals(0, s.getElemente().indexOf(element), "Das Doppeltor soll das erste Element sein");
  };

export const testEinElementRechtsInEinerAnsonstenLeerenStreckeWirdAufDoppeltorGeändert: Test =
  async () => {
    resetArrangement();
    const agt = APP.arrangement;
    agt.grundriss.setStandardForm("Strecke", [5]);
    const s = agt.segmente[0];
    const element = agt.createDefaultElement();
    s.addPart(element);
    element.snap = 1;
    agt.update3d();
    APP.arrangement.setSelectedObjects([element]);
    agt.update3d();
    const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
    agt.changeSelectedZaunElemente({
      massAuswahlwert,
    });
    element.behalteLichteweite(
      () => (element.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    assertDoppeltorInSzene(element, s);
    assert.equals(0, s.getElemente().indexOf(element), "Das Doppeltor soll das erste Element sein");
  };

function assertDoppeltorInSzene(element: ZElement, s: Segment, mitEAntrieb = false) {
  assert.isTrue(s.getElemente().includes(element), "Das Doppeltor wurde gelöscht");
  assert.isTrue(element?.isDoppeltor, "ist kein Doppeltor");
  assert.equals(
    element.flügelsummenbreite + (mitEAntrieb ? 0.06 : 0.04),
    element.lichteWeite,
    `die Lichteweite soll Flügelsummenbreite + ${mitEAntrieb ? 6 : 4} cm sein`
  );
}

// export const test2440mmMauerLinksChangeLeerraumToDoppeltor: Test = async () => {
//   if (isVoreinstellungMitEAntrieb) {
//
//   }
//   const { agt, s } = createStreckeWithMauerUndBreite(2.44, { links: true, rechts: true });
//   const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
//   APP.arrangement.setSelectedObjects(s.empties);
//   agt.update3d();
//   agt.changeSelectedZaunElemente({ massAuswahlwert });
//   agt.update3d();
//   const element = s.getElemente()[0];
//   assertDoppeltorInSzene(element, s);
//   assertSegmentIstLückenlosGefüllt(s);
// };

export const test2500mmMauerLinksChangeLeerraumToDoppeltor: Test = async () => {
  const { agt, s } = createStreckeWithMauerUndBreite(2.5, { links: true, rechts: true });
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  APP.arrangement.setSelectedObjects(s.empties);
  agt.update3d();
  agt.changeSelectedZaunElemente({ massAuswahlwert });
  agt.update3d();
  const element = s.getElemente()[0];
  element.behalteLichteweite(
    () => (element.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  agt.update3d();
  assertDoppeltorInSzene(element, s);
  assert.equals(1, s.getElemente().length);
  assert.equals(2.44, element.lichteWeite);
};

export const test2500mmMauerLinksRechtsChangeElementToDoppeltor: Test = async () => {
  const { agt, s } = createStreckeWithMauerUndBreite(2.5, { links: true, rechts: true });
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  let element = s.getElemente()[0];
  APP.arrangement.setSelectedObjects(element);
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  agt.update3d();
  agt.changeSelectedZaunElemente({ massAuswahlwert });
  agt.update3d();
  element = agt.selectedObjects[0];
  element.behalteLichteweite(
    () => (element.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  agt.update3d();
  assertDoppeltorInSzene(element, s);
  assert.equals(1, s.getElemente().length);
  assert.equals(2.44, element.lichteWeite);
};

export const test2440mmMauerLinksRechtsChangeElementToDoppeltor: Test = async () => {
  const { agt, s } = createStreckeWithMauerUndBreite(2.44, { links: true, rechts: true });
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  const element = s.getElemente()[0];
  APP.arrangement.setSelectedObjects(element);
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  agt.update3d();
  agt.changeSelectedZaunElemente({ massAuswahlwert });
  agt.update3d();
  const doppeltor = agt.selectedObjects[0];
  doppeltor.behalteLichteweite(
    () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  agt.update3d();
  assertDoppeltorInSzene(doppeltor, s);
  // ACHTUNG! Rausgenommen, weil die initiale Breite des Doppeltores etwas zu schmal ist, dürfte dasselbe Problem sein wie test2500mmMauerLinksRechtsChangeElementToDoppeltor
  assertSegmentIstLückenlosGefüllt(s);
};

export const testFreistehendesDoppeltorLeerraumRechts: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  setSternFormMitWinkelnLeerraumRechts(3, [0]);
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const doppeltor = agt.getElemente()[0];
  agt.setSelectedObjects([doppeltor]);
  const füllungAuswahlwert = doppeltor.füllungAuswahlwert;
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  doppeltor.behalteLichteweite(
    () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  assert.equals("Individuell", doppeltor.segment.verteilungsstrategieAuswahlwert_.Key);
  doppeltor.segment.verteilungsstrategieAuswahlwert_ =
    APP.richMetadata.findAuswahlwert("LeerraumRechts");
  agt.update3d();
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getPfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
};

export const testEinfügenInLeerraumFreistehendesDoppeltorIndividuell: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  setSternFormMitWinkelnLeerraumRechts(3, [0]);
  const s = agt.segmente[0];
  s.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Individuell");
  agt.update3d();
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const leerraum = s.empties[0];
  agt.setSelectedObjects([leerraum]);
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const doppeltor = agt.getElemente()[0];
  assert.equals(2.87, doppeltor.breite);
  assert.equals(1, agt.getTore().length, "Anzahl Tore");
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getPfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
  await subTestContext("Wechsel auf E_Antrieb_ohne", () => {
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    assert.equals(2.89, doppeltor.breite);
    assertWarnungen(agt, []);
    assertSegmentIstLückenlosGefüllt(s);
  });
  await subTestContext("Wechsel auf E_Antrieb_mit", () => {
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_mit"))
    );
    agt.update3d();
    agt.update3d();
    assert.equals(2.87, doppeltor.breite);
    assertWarnungen(agt, []);
    assertSegmentIstLückenlosGefüllt(s);
  });
};

export const testWechselElementAufFreistehendesDoppeltorIndividuell: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  setSternFormMitWinkelnLeerraumRechts(3, [0]);
  const s = agt.segmente[0];
  s.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Individuell");
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const element = agt.getElemente()[0];
  const füllungAuswahlwert = element.füllungAuswahlwert;
  agt.setSelectedObjects([element]);
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  agt.update3d();
  const doppeltor = agt.selectedObjects[0];
  assert.equals(2.87, doppeltor.breite);
  assert.equals(1, agt.getTore().length, "Anzahl Tore");
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getPfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
};

export const testWechselElementAufFreistehendesDoppeltorGleichmässig: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  setSternFormMitWinkelnLeerraumRechts(3, [0]);
  const s = agt.segmente[0];
  s.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.update3d();
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const element = agt.getElemente()[0];
  agt.setSelectedObjects([element]);
  const füllungAuswahlwert = element.füllungAuswahlwert;
  assert.equals(
    "2143 SYSTEM WPC CLASSIC  Anthrazit/Anthrazit Zaunfeld-Set 178 x 183 cm",
    element.meta.Key
  );
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  const doppeltor = agt.selectedObjects[0];
  assert.equals("Individuell", doppeltor.segment.verteilungsstrategieAuswahlwert_.Key);
  doppeltor.segment.verteilungsstrategieAuswahlwert_ =
    APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.update3d();
  assert.equals(2.87, doppeltor.breite);
  assert.equals(1, agt.getTore().length, "Anzahl Tore");
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getPfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
  await subTestContext("Wechsel auf E_Antrieb_ohne", () => {
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
    );
    agt.update3d();
    agt.update3d();
    assert.equals(2.89, doppeltor.breite);
    assertWarnungen(agt, []);
    assertSegmentIstLückenlosGefüllt(s);
  });
  await subTestContext("Wechsel auf E_Antrieb_mit", () => {
    doppeltor.behalteLichteweite(
      () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_mit"))
    );
    agt.update3d();
    agt.update3d();
    assert.equals(2.87, doppeltor.breite);
    assertWarnungen(agt, []);
    assertSegmentIstLückenlosGefüllt(s);
  });
};

export const testDoppeltorInKastenGleichmässig: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  agt.grundriss.ecken = streckenZuGraph([
    [v2(0, 0), v2(0, 2)],
    [v2(0, 0), v2(3, 0)],
    [v2(3, 0), v2(3, 2)],
  ]).ecken;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.update3d();
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  let doppeltor = agt.getElemente()[2];
  agt.setSelectedObjects([doppeltor]);
  const füllungAuswahlwert = doppeltor.füllungAuswahlwert;
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  doppeltor = agt.selectedObjects[0];
  doppeltor.behalteLichteweite(
    () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  doppeltor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert("Öffnung_nach_außen");
  assert.equals("Individuell", doppeltor.segment.verteilungsstrategieAuswahlwert_.Key);
  doppeltor.segment.verteilungsstrategieAuswahlwert_ =
    APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.update3d();
  assert.equals(1, agt.getTore().length, "Anzahl Tore");
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getTorpfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
};

export const testDoppeltorInBreitemZaunGleichmässig: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  setSternFormMitWinkelnLeerraumRechts(12, [0]);
  agt.segmente[0].verteilungsstrategieAuswahlwert_ =
    APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.update3d();
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  const doppeltor = agt.getElemente()[3];
  const füllungAuswahlwert = doppeltor.füllungAuswahlwert;
  agt.setSelectedObjects([doppeltor]);
  agt.changeSelectedZaunElemente({
    massAuswahlwert,
    füllungAuswahlwert,
  });
  assert.equals("Individuell", doppeltor.segment.verteilungsstrategieAuswahlwert_.Key);
  doppeltor.segment.verteilungsstrategieAuswahlwert_ =
    APP.richMetadata.findAuswahlwert("Gleichmässig");
  doppeltor.behalteLichteweite(
    () => (doppeltor.eAntriebAuswahlwert = APP.richMetadata.findAuswahlwert("E_Antrieb_ohne"))
  );
  agt.update3d();
  assert.deepEquals(
    [
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
      "E1080 Torpfosten einbetonieren 180 anthrazit Aufhängeseite",
    ],
    APP.arrangement.getTorpfosten().map((p) => p.meta?.Key)
  );
  assertWarnungen(agt, []);
};

export const testDoppeltorInStreckeOhneLücken: Test = async () => {
  assert.isTrue(isVoreinstellungMitEAntrieb, "isVoreinstellungMitEAntrieb");
  const agt = getInspiredArrangement("Inspiration SYSTEM WPC CLASSIC Anthrazit");
  agt.pfostenartAuswahlwert = APP.richMetadata.findAuswahlwert("Klemmpfosten");
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [5]);
  const s = agt.segmente[0];
  agt.update3d();
  const tor = agt.createElementFromJson({
    form: "Doppeltor",
    füllungAuswahlwert: "SYSTEM WPC CLASSIC Anthrazit",
    nimmStandardSize: true,
    eAntriebAuswahlwert: "E_Antrieb_ohne",
  });
  insertTor(s, 1, s.breite, tor);
  agt.update3d();
  assertWarnungen(agt, []);
  // hier war ein Fehler, UMProfil wurde in getZwischenabstandVerbose nicht berücksichtigt
  agt.pfostenartAuswahlwert = null;
  agt.update3d();
  assertWarnungen(agt, []);
  agt;
  APP.arrangement.auswahlAccessoren.Pfostenart.setter(
    APP.richMetadata.findAuswahlwert("Steckpfosten")
  );
  agt.update3d();
  assertWarnungen(agt, ["Metalltorpfosten", "Metalltorpfosten"]);
  APP.arrangement.auswahlAccessoren.Pfostenart.setter(
    APP.richMetadata.findAuswahlwert("Metallpfosten")
  );
  agt.update3d();
  assertWarnungen(agt, ["Metalltorpfosten", "Metalltorpfosten"]);
};
